/**
 * Expense mutations hooks
 * Handles create, update, and delete operations for property expenses
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { Expense } from "@/types/admin/expense";

/**
 * Invalidate all expense-related queries
 */
function invalidateExpenseQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ["expenses"] });
  queryClient.invalidateQueries({ queryKey: ["dashboard-aggregates"] });
}

/**
 * Hook to create a new expense
 */
export const useCreateExpense = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (
      expense: Omit<
        Expense,
        | "id"
        | "created_at"
        | "updated_at"
        | "properties"
        | "category"
        | "subcategory"
      >,
    ) => {
      const { data, error } = await supabase
        .from("properties_expenses")
        .insert(expense)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      invalidateExpenseQueries(queryClient);
      toast({
        title: "Gasto creado",
        description: "El gasto ha sido creado exitosamente",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "No se pudo crear el gasto",
        variant: "destructive",
      });
      console.error("Error creating expense:", error);
    },
  });
};

/**
 * Hook to update an existing expense
 */
export const useUpdateExpense = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      ...expense
    }: Partial<Expense> & { id: string }) => {
      const { data, error } = await supabase
        .from("properties_expenses")
        .update(expense)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      invalidateExpenseQueries(queryClient);
      toast({
        title: "Gasto actualizado",
        description: "El gasto ha sido actualizado exitosamente",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "No se pudo actualizar el gasto",
        variant: "destructive",
      });
      console.error("Error updating expense:", error);
    },
  });
};

/**
 * Hook to delete an expense
 */
export const useDeleteExpense = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("properties_expenses")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      invalidateExpenseQueries(queryClient);
      toast({
        title: "Gasto eliminado",
        description: "El gasto ha sido eliminado exitosamente",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "No se pudo eliminar el gasto",
        variant: "destructive",
      });
      console.error("Error deleting expense:", error);
    },
  });
};
